import { translate, useMatchSmallViewport } from '@avensia/excite';
import { Overlay } from '@avensia/excite-ui';
import { styled } from '@glitz/react';
import * as React from 'react';
import { ChevronRight } from 'svenssons/shared/icons';
import { scrollbarDecorator } from 'webapp/decorators/scrollbar';
import { Button } from 'webapp/components/button';
import { CloseIcon } from 'webapp/components/icons';
import { bareListDecorator } from 'webapp/decorators/list';
import { overflowYDecorator } from 'webapp/decorators/overflow';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { white } from 'webapp/shared/sharedTheme';
import { Theme } from 'webapp/theme';
import { useVariantSelector } from 'webapp/variant-selectors/ProductVariantsSelectorProvider';
import { ZIndex } from 'webapp/z-index';
import { NnTestIdProps } from 'webapp/shared/test-ids';

type MobileVariantSelectorPropType = {
  id: string;
  selectedOption: NordicNest.Api.SelectOptionApiModel | undefined;
  value: string | undefined;
  placeholder?: string;
  optionTransform?: (option: NordicNest.Api.SelectOptionApiModel) => React.ReactNode;
  selectedTransform?: (option: NordicNest.Api.SelectOptionApiModel) => React.ReactNode;
  open: (selectorId: string) => void;
  onRemoveOption?: () => void;
};

export const MobileVariantSelector = styled(
  ({
    value,
    selectedOption,
    open,
    selectedTransform,
    placeholder,
    id,
    onRemoveOption,
    ...restProps
  }: MobileVariantSelectorPropType) => (
    <Base {...restProps}>
      <input type="hidden" value={value} />
      <SelectedValue onClick={() => open(id)}>
        {!value && placeholder}
        {selectedOption && (selectedTransform ? selectedTransform(selectedOption) : selectedOption.text)}
        {onRemoveOption && selectedOption ? (
          <CloseIcon
            onClick={e => {
              e.stopPropagation();
              onRemoveOption?.();
            }}
          />
        ) : (
          <Arrow />
        )}
      </SelectedValue>
    </Base>
  ),
);

export function VariantMobileOptions() {
  const { isOpen, selectors, selectOption, close } = useVariantSelector();
  const isSmallViewport = useMatchSmallViewport();
  return isSmallViewport ? (
    <>
      {selectors.map(({ options, id, optionTransform, value, placeholder }) => (
        <Options key={id} zIndex={ZIndex.Overlay} onClose={close} isEnabled={isOpen(id)}>
          <Container css={!isOpen(id) && hiddenDecorator}>
            <Header>
              <Label>{placeholder}</Label>
              <CloseButton
                as={Theme.Transparent}
                onClick={close}
                square
                aria-label={translate(t => t.ariaLabelCloseButton)}
              >
                <CloseIcon />
              </CloseButton>
            </Header>
            <ScrollContainer>
              <Dropdown role="listbox" {...NnTestIdProps.ProductVariationMobileOptionContainer}>
                {options?.map(option => (
                  <Option
                    css={option.value === value && selectDecorator}
                    onClick={() => selectOption(option.value)}
                    role="option"
                    aria-selected={value === option.value ? true : false}
                    key={option.value}
                  >
                    {optionTransform ? optionTransform(option) : option.text}
                  </Option>
                ))}
              </Dropdown>
            </ScrollContainer>
          </Container>
        </Options>
      ))}
    </>
  ) : null;
}

const Base = styled.div();

const hiddenDecorator = styled({
  display: 'none',
});

const Header = styled.div({
  backgroundColor: white,
  display: 'grid',
  gridTemplateAreas: '". label close"',
  gridTemplateColumns: '58px 2fr 58px',
  textAlign: 'center',
  fontSize: t => t.sharedFontSize.m,
  border: { bottom: { width: 1, style: 'solid', color: t => t.borderStrongColor } },
  alignItems: 'center',
  height: 72,
});

const Label = styled.div({
  fontSize: t => t.sharedFontSize.m,
  gridArea: 'label',
});

const CloseButton = styled(Button, {
  gridArea: 'close',
  padding: { x: t => t.sharedSpacing.m },
});

const selectDecorator = styled({
  backgroundColor: t => t.monochrome.color,
});

const Container = styled.div({
  position: 'absolute',
  width: '100%',
  bottom: 0,
  backgroundColor: white,
  overflow: 'hidden',
});

const ScrollContainer = styled.div(scrollbarDecorator, overflowYDecorator, {
  maxHeight: '55vh',
});

const Dropdown = styled.ul(bareListDecorator, {
  backgroundColor: white,
  width: '100%',
  height: 'inherit',
});

const Option = styled.li(overflowEllipsisDecorator, {
  cursor: 'pointer',
  borderBottom: { width: 'thin', style: 'solid', color: t => t.borderStrongColor },
  padding: { x: t => t.sharedSpacing.m, y: t => t.sharedSpacing.s },
  ':last-child': {
    borderBottom: { width: 0 },
  },
});

const Options = styled(Overlay);

const SelectedValue = styled.div({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: { x: t => t.sharedSpacing.m, y: t => t.sharedSpacing.xs },
  border: { xy: { width: 'thin', style: 'solid', color: t => t.borderStrongColor } },
  height: 56,
});

const Arrow = styled(ChevronRight, {
  flexShrink: 0,
});
